import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ParticipantResponseStatusesEnum} from '../../../models/api/participantModel';

/*
 * Props.
 */

interface ResponseStatusDropdownBoxProps {
  responseStatus: ParticipantResponseStatusesEnum;
  onResponseStatusChange: HandlerOf<ParticipantResponseStatusesEnum>;
  className?: string;
}

/*
 * Styles.
 */

const StyledContentWrapperDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-right: 12px;
`;

const StyledDropdownList = styled(DropdownList)`
  min-width: 125px;
`;

const StyledAttendingDiv = styled.div`
  margin-right: 3px;
`;

/*
 * Component.
 */

export const ResponseStatusDropdownBox: FC<ResponseStatusDropdownBoxProps> = (props) => (
  <DropdownBoxCoordinator
    value={renderContent(props.responseStatus)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
    boxClassName={props.className}
  />
);

function renderContent(responseStatus: ParticipantResponseStatusesEnum) {
  return (
    <StyledContentWrapperDiv>
      {renderIcon(responseStatus)}
      {renderText(responseStatus)}
    </StyledContentWrapperDiv>
  );
}

function renderIcon(responseStatus: ParticipantResponseStatusesEnum) {
  switch (responseStatus) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return (
        <UserThemeConsumer>
          {(theme) => <StyledIcon name="checkmarkYesActivity" color={theme.palette.green.shade40} />}
        </UserThemeConsumer>
      );
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return (
        <UserThemeConsumer>
          {(theme) => <StyledIcon name="questionmarkMaybeActivity" color={theme.palette.orange.shade40} />}
        </UserThemeConsumer>
      );
    case ParticipantResponseStatusesEnum.DECLINED:
      return (
        <UserThemeConsumer>
          {(theme) => <StyledIcon name="xNoActivity" color={theme.palette.red.shade40} />}
        </UserThemeConsumer>
      );
    default:
      return null;
  }
}

function renderText(responseStatus: ParticipantResponseStatusesEnum) {
  switch (responseStatus) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return <Trans id="response-status-dropdown-yes">Yes</Trans>;
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return <Trans id="response-status-dropdown-maybe">Maybe</Trans>;
    case ParticipantResponseStatusesEnum.DECLINED:
      return <Trans id="response-status-dropdown-no">No</Trans>;
    default:
      return (
        <StyledAttendingDiv>
          <Trans id="response-status-dropdown-attending">Attending?</Trans>
        </StyledAttendingDiv>
      );
  }
}

function renderDropdown(popoverProps: PopoverTransferProps, props: ResponseStatusDropdownBoxProps) {
  const responseStatuses = [
    ParticipantResponseStatusesEnum.ACCEPTED,
    ParticipantResponseStatusesEnum.TENTATIVE,
    ParticipantResponseStatusesEnum.DECLINED,
  ];
  return (
    <Dropdown {...popoverProps}>
      <StyledDropdownList {...popoverProps}>
        {responseStatuses.map((rs) => renderItem(rs, props))}
      </StyledDropdownList>
    </Dropdown>
  );
}

function renderItem(responseStatus: ParticipantResponseStatusesEnum, props: ResponseStatusDropdownBoxProps) {
  const isChecked = responseStatus === props.responseStatus;

  return (
    <DropdownItem
      key={responseStatus}
      isChecked={isChecked}
      onSelect={() => props.onResponseStatusChange(responseStatus)}
    >
      <DropdownItemContent isChecked={isChecked}>{renderContent(responseStatus)}</DropdownItemContent>
    </DropdownItem>
  );
}
